import { Button, Icon, SegmentedCard } from '@smartgift/frontend-toolkit';
import styled, { css } from 'styled-components';

import { Z_INDEX } from '@hero/constants';

const ShoppingCart = styled.div`
  bottom: 0;
  position: fixed;
  top: 0;
  width: 300px;
  z-index: ${Z_INDEX.drawer};
`;

const ProductCardDrawerWrapper = styled.div`
  background: var(--color-bg-body);
  display: block;
  height: 100%;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  width: 100%;
  z-index: 3;

  ${({ openIn }) =>
    openIn &&
    css`
      transform: translateX(0);
    `}}
`;

const ProductCardItemClearArea = styled.div`
  border-bottom: 1px solid var(--color-accent-secondary-weaker);
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 8px 16px;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-left: 16px;
  padding-right: 24px;
  padding-top: 8px;
`;

const FloatingCartButtonBadge = styled.span`
  align-items: center;
  background: var(--color-components-button-primary-outlined-fg);
  border-radius: var(--size-border-radius-circle);
  color: var(--color-components-button-primary-outlined-bg);
  display: flex;
  font-size: 14px;
  font-weight: 700;
  height: 24px;
  justify-content: center;
  line-height: 150%;
  text-align: center;
  width: 24px;
`;

const FloatingCartButton = styled(Button)`
  background-color: var(--color-components-button-primary-outlined-bg);
  border-radius: var(--size-border-radius-circle);
  box-shadow: var(--shadow-l);
  color: var(--color-fg-default);
  display: ${({ isFloatingButtonShown }) => (isFloatingButtonShown ? 'block' : 'none')};
  height: 48px;
  justify-content: space-between;
  min-width: auto;
  padding: 0 var(--size-16) 0 var(--size-12);
  position: fixed;
  transition: all 0.3s ease;
  width: 95px;

  &:hover {
    color: var(--color-fg-default);

    ${FloatingCartButtonBadge} {
      background: var(--color-components-button-primary-outlined-fg-hover);
      color: var(--color-components-button-primary-outlined-bg-hover);
    }

    ${Icon.S.Icon} {
      color: var(--color-components-button-primary-outlined-fg-hover);
    }
  }

  ${({ isTransitioning, opened, location }) => {
    return css`
      bottom: ${location?.x || 16}px;
      opacity: ${!isTransitioning && !opened ? 1 : 0};
      right: ${location?.y || 16}px;
    `;
  }}
`;

const CartEmpty = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 55px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px;
  text-align: center;
`;

const WidgetHeader = styled.div`
  background: var(--color-accent-secondary-weakest);
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 5px;
  padding: 16px;
`;

const WidgetHeaderTitleHead = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
`;

const WidgetHeaderTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const WidgetFooter = styled.div`
  background: var(--color-accent-secondary-weakest);
  border: var(--color-accent-secondary-weaker);
  border-top: 1px solid var(--color-accent-secondary-weaker);
  display: flex;
  flex-direction: column;
  left: 1px;
  width: 100%;
`;

const WidgetFooterSubtotal = styled.div`
  background: var(--color-bg-weaker);
  border-bottom: 1px solid var(--color-accent-secondary-weaker);
  border-top: 1px solid var(--color-accent-secondary-weaker);
  padding: 16px;

  span {
    float: right;
  }
`;

const FooterActionButtons = styled.div`
  background: var(--color-bg-weaker);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;

  ${Button.S.Button} {
    font-size: 14px;
  }
`;

const ChooseFeatureType = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FeatureTypeWrapper = styled.div`
  display: flex;
  flex-direction: row;

  ${SegmentedCard.S.SegmentedCard} {
    width: 100%;
  }
`;

const CheckedBox = styled.div`
  align-items: center;
  border-radius: var(--size-border-radius-circle);
  display: flex;
  height: 20px;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 8px;
  width: 20px;
  ${({ active }) => css`
    background: var(--color-${active ? 'primary-500' : 'navy-100'});
  `}
`;

const FeatureInfo = styled.div`
  align-items: center;
  align-self: stretch;
  background: var(--color-accent-secondary-weakest);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  display: flex;
  gap: 4px;
  padding: 8px;
`;

const ProductCardDrawer = styled.div`
  background: var(--color-bg-body);
  border-left: 1px solid var(--color-accent-secondary-weaker);
  border-top: 1px solid var(--color-accent-secondary-weaker);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const WidgetFooterFeeBreakdownModalButton = styled(Button)`
  color: var(--color-fg-weaker);
  font-size: 12px;
  margin-top: 8px;
`;

export {
  CartEmpty,
  CheckedBox,
  ChooseFeatureType,
  FeatureInfo,
  FeatureTypeWrapper,
  FloatingCartButton,
  FloatingCartButtonBadge,
  FooterActionButtons,
  ProductCardDrawer,
  ProductCardDrawerWrapper,
  ProductCardItemClearArea,
  ProductList,
  ShoppingCart,
  WidgetFooter,
  WidgetFooterFeeBreakdownModalButton,
  WidgetFooterSubtotal,
  WidgetHeader,
  WidgetHeaderTitle,
  WidgetHeaderTitleHead
};
